/**
 * NewOrderDialog — creazione/modifica ordine attrezzatura.
 * Catalogo raggruppato per categoria con ricerca, quantità +/- e totale live.
 * Con `editOrder` passato, il negozio è fisso e il submit aggiorna
 * l'ordine esistente (PUT) invece di crearne uno nuovo (POST).
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Minus, Plus, Search, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { fmtPrice } from '../pages/OrdersPage';

export default function NewOrderDialog({ open, onClose, stores, onCreated, editOrder = null }) {
  const isEdit = !!editOrder;
  const [catalog, setCatalog] = useState(null);
  const [search, setSearch] = useState('');
  const [storeId, setStoreId] = useState('');
  const [qty, setQty] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      const initial = {};
      editOrder.items.forEach(it => { initial[it.product_id] = it.qty; });
      setQty(initial);
      setNotes(editOrder.notes || '');
      setSearch('');
      setStoreId(editOrder.store_id);
    } else {
      setQty({}); setNotes(''); setSearch('');
      if (stores.length === 1) setStoreId(stores[0].store_id);
    }
    api.get('/orders/catalog')
      .then(r => setCatalog(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Errore caricamento catalogo'));
  }, [open, stores, isEdit, editOrder]);

  const grouped = useMemo(() => {
    if (!catalog) return [];
    const s = search.trim().toLowerCase();
    const filtered = s
      ? catalog.filter(p => p.name.toLowerCase().includes(s) || (p.code || '').toLowerCase().includes(s))
      : catalog;
    const map = new Map();
    filtered.forEach(p => {
      if (!map.has(p.category)) map.set(p.category, []);
      map.get(p.category).push(p);
    });
    return [...map.entries()];
  }, [catalog, search]);

  const cart = useMemo(() => {
    const lines = (catalog || []).filter(p => (qty[p.product_id] || 0) > 0);
    const total = lines.reduce((acc, p) => acc + (p.price != null ? p.price * qty[p.product_id] : 0), 0);
    const unpriced = lines.filter(p => p.price == null).length;
    const count = lines.reduce((acc, p) => acc + qty[p.product_id], 0);
    return { lines, total, unpriced, count };
  }, [catalog, qty]);

  const setQ = (pid, v) => setQty(prev => ({ ...prev, [pid]: Math.max(0, Math.min(999, v)) }));

  const submit = async () => {
    if (!storeId) { toast.error('Seleziona il negozio'); return; }
    if (cart.lines.length === 0) { toast.error('Aggiungi almeno un articolo'); return; }
    setSaving(true);
    try {
      const items = cart.lines.map(p => ({ product_id: p.product_id, qty: qty[p.product_id] }));
      if (isEdit) {
        await api.put(`/orders/${editOrder.order_id}`, { items, notes: notes.trim() || null });
        toast.success('Ordine aggiornato');
      } else {
        await api.post('/orders', { store_id: storeId, items, notes: notes.trim() || null });
        toast.success('Ordine inviato — in attesa di approvazione');
      }
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEdit ? "Errore nell'aggiornamento dell'ordine" : "Errore nell'invio dell'ordine"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="new-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShoppingCart className="w-5 h-5" /> {isEdit ? 'Modifica ordine' : 'Nuovo ordine'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Aggiungi, rimuovi o modifica le quantità degli articoli dell'ordine ${editOrder.number}.`
              : "Scegli il negozio e gli articoli dal catalogo. L'ordine verrà inviato per approvazione."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">Negozio *</label>
            {isEdit ? (
              <div className="h-9 flex items-center px-3 border border-slate-200 bg-slate-50 text-sm text-slate-700" data-testid="edit-order-store-fixed">
                {editOrder.store_name}
              </div>
            ) : (
              <Select value={storeId} onValueChange={setStoreId}>
                <SelectTrigger className="rounded-none" data-testid="order-store-select">
                  <SelectValue placeholder="Scegli negozio…" />
                </SelectTrigger>
                <SelectContent>
                  {[...stores].sort((a, b) => (a.display_name || '').localeCompare(b.display_name || '', 'it')).map(s => (
                    <SelectItem key={s.store_id} value={s.store_id} data-testid={`order-store-opt-${s.store_id}`}>
                      {s.display_name || s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">Cerca articolo</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Nome o codice…"
                     className="rounded-none pl-8" data-testid="order-search-input" />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto border border-slate-200 divide-y divide-slate-100 min-h-[200px]" data-testid="order-catalog-list">
          {catalog === null ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : grouped.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-10">Nessun articolo trovato.</p>
          ) : grouped.map(([cat, prods]) => (
            <div key={cat}>
              <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 sticky top-0">{cat}</p>
              {prods.map(p => {
                const q = qty[p.product_id] || 0;
                return (
                  <div key={p.product_id} className={`flex items-center gap-2 px-3 py-2 ${q > 0 ? 'bg-emerald-50/60' : ''}`}
                       data-testid={`catalog-product-${p.product_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-800 truncate">{p.name}</p>
                      <p className="text-[11px] text-slate-400 font-mono">{p.code || '—'}</p>
                    </div>
                    <span className="text-sm font-medium text-slate-700 w-20 text-right shrink-0">
                      {p.price != null ? fmtPrice(p.price) : <span className="text-[10px] text-amber-600 font-semibold">{p.price_note || 'N/D'}</span>}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="outline" size="icon" className="h-7 w-7 rounded-none" disabled={q === 0}
                              onClick={() => setQ(p.product_id, q - 1)} data-testid={`qty-minus-${p.product_id}`}>
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className={`w-8 text-center text-sm font-semibold ${q > 0 ? 'text-emerald-700' : 'text-slate-400'}`}
                            data-testid={`qty-value-${p.product_id}`}>{q}</span>
                      <Button variant="outline" size="icon" className="h-7 w-7 rounded-none"
                              onClick={() => setQ(p.product_id, q + 1)} data-testid={`qty-plus-${p.product_id}`}>
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">Note (opzionale)</label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} maxLength={1000}
                    placeholder="Es. urgente, sostituzione tosatrice rotta…"
                    className="rounded-none resize-none" data-testid="order-notes-input" />
        </div>

        <DialogFooter className="items-center gap-2 sm:justify-between">
          <div className="text-sm text-slate-700 mr-auto" data-testid="order-cart-summary">
            <span className="font-semibold">{cart.count}</span> articoli — Totale{' '}
            <span className="font-bold">{fmtPrice(cart.total)}</span>
            {cart.unpriced > 0 && <span className="text-xs text-amber-600 ml-1">(+{cart.unpriced} senza prezzo)</span>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button onClick={submit} disabled={saving || cart.count === 0 || !storeId} className="gap-1.5" data-testid="order-submit-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Salva modifiche' : 'Invia ordine'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
